/**
 * A QR code encoder.
 *
 * Phase 7.4 needs a QR code for printed signage, and that code has to work with
 * nothing configured: no API key, no third-party QR service that can see every
 * event URL, and no network call from a host who is offline in a marquee. So the
 * encoder lives here, in the browser, and produces the module matrix directly.
 *
 * Scope is deliberately narrow: byte mode, error correction level M, versions
 * 1 to 10. That is 26 to 346 total codewords, which covers a guest link up to
 * roughly 270 characters. Every link this product generates is a `/u/<code>`
 * path, so version 2 is the normal case and version 10 is a ceiling that exists
 * to fail loudly rather than silently.
 *
 * Correctness is checked against an independent implementation — see
 * scripts/verify/verify-qr.py. An encoder that only agrees with itself is not verified,
 * and a QR code that does not scan is worse than no QR code at all.
 */

const ECL = "M";

/* ------------------------------------------------------------------ */
/* Tables                                                              */
/* ------------------------------------------------------------------ */

/** Total codewords (data + error correction) per version, versions 1-10. */
const TOTAL_CODEWORDS = [0, 26, 44, 70, 100, 134, 172, 196, 242, 292, 346];

/**
 * Level M block structure per version: [ecPerBlock, group1Blocks, group1Data, group2Blocks, group2Data].
 */
const BLOCKS_M: ReadonlyArray<readonly [number, number, number, number, number]> = [
  [0, 0, 0, 0, 0],
  [10, 1, 16, 0, 0],
  [16, 1, 28, 0, 0],
  [26, 1, 44, 0, 0],
  [18, 2, 32, 0, 0],
  [24, 2, 43, 0, 0],
  [16, 4, 27, 0, 0],
  [18, 4, 31, 0, 0],
  [22, 2, 38, 2, 39],
  [22, 3, 36, 2, 37],
  [26, 4, 43, 1, 44],
];

/** Alignment pattern centre coordinates per version. */
const ALIGNMENT: ReadonlyArray<readonly number[]> = [
  [],
  [],
  [6, 18],
  [6, 22],
  [6, 26],
  [6, 30],
  [6, 34],
  [6, 22, 38],
  [6, 24, 42],
  [6, 26, 46],
  [6, 28, 50],
];

const MAX_VERSION = 10;

/* ------------------------------------------------------------------ */
/* Galois field arithmetic                                              */
/* ------------------------------------------------------------------ */

/** GF(256) with the QR primitive polynomial x^8 + x^4 + x^3 + x^2 + 1 (0x11D). */
const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);
(() => {
  let x = 1;
  for (let i = 0; i < 255; i++) {
    EXP[i] = x;
    LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255]!;
})();

function gfMul(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return EXP[LOG[a]! + LOG[b]!]!;
}

/** Generator polynomial for `degree` error correction codewords. */
function generatorPoly(degree: number): Uint8Array {
  let poly = new Uint8Array([1]);
  for (let i = 0; i < degree; i++) {
    const next = new Uint8Array(poly.length + 1);
    for (let j = 0; j < poly.length; j++) {
      next[j]! ^= poly[j]!;
      next[j + 1]! ^= gfMul(poly[j]!, EXP[i]!);
    }
    poly = next;
  }
  return poly;
}

/** Reed-Solomon remainder: the ECC codewords for one block. */
function reedSolomon(data: Uint8Array, ecCount: number): Uint8Array {
  const gen = generatorPoly(ecCount);
  const remainder = new Uint8Array(ecCount);

  for (const byte of data) {
    const factor = byte ^ remainder[0]!;
    remainder.copyWithin(0, 1);
    remainder[ecCount - 1] = 0;
    for (let i = 0; i < ecCount; i++) {
      remainder[i]! ^= gfMul(gen[i + 1]!, factor);
    }
  }

  return remainder;
}

/* ------------------------------------------------------------------ */
/* Bit stream                                                          */
/* ------------------------------------------------------------------ */

class BitBuffer {
  readonly bits: number[] = [];

  push(value: number, length: number): void {
    for (let i = length - 1; i >= 0; i--) this.bits.push((value >>> i) & 1);
  }

  get length(): number {
    return this.bits.length;
  }
}

/** Data codewords available at level M for a version. */
function dataCodewords(version: number): number {
  const [, g1Blocks, g1Data, g2Blocks, g2Data] = BLOCKS_M[version]!;
  return g1Blocks * g1Data + g2Blocks * g2Data;
}

/** Smallest version that holds `byteLength` bytes of byte-mode payload. */
function chooseVersion(byteLength: number): number {
  for (let version = 1; version <= MAX_VERSION; version++) {
    // 4 mode bits + count field + payload + the 4-bit terminator.
    const countBits = version < 10 ? 8 : 16;
    const needed = 4 + countBits + byteLength * 8 + 4;
    if (needed <= dataCodewords(version) * 8) return version;
  }
  throw new Error(`QR payload too long: ${byteLength} bytes exceeds version ${MAX_VERSION}.`);
}

/** Encode the payload into data codewords, with padding. */
function buildCodewords(bytes: Uint8Array, version: number): Uint8Array {
  const capacity = dataCodewords(version);
  const buffer = new BitBuffer();

  buffer.push(0b0100, 4); // byte mode
  buffer.push(bytes.length, version < 10 ? 8 : 16);
  for (const byte of bytes) buffer.push(byte, 8);

  // Terminator, then pad to a byte boundary.
  buffer.push(0, Math.min(4, capacity * 8 - buffer.length));
  while (buffer.length % 8 !== 0) buffer.bits.push(0);

  const out = new Uint8Array(capacity);
  for (let i = 0; i < buffer.length; i += 8) {
    let byte = 0;
    for (let j = 0; j < 8; j++) byte = (byte << 1) | buffer.bits[i + j]!;
    out[i / 8] = byte;
  }

  // Alternating pad codewords, as the specification requires.
  const PAD = [0xec, 0x11];
  for (let i = buffer.length / 8, p = 0; i < capacity; i++, p++) out[i] = PAD[p % 2]!;

  return out;
}

/** Split into blocks, add ECC, and interleave. */
function interleave(data: Uint8Array, version: number): Uint8Array {
  const [ecPerBlock, g1Blocks, g1Data, g2Blocks, g2Data] = BLOCKS_M[version]!;

  const blocks: Uint8Array[] = [];
  const ecBlocks: Uint8Array[] = [];
  let offset = 0;

  for (let i = 0; i < g1Blocks + g2Blocks; i++) {
    const size = i < g1Blocks ? g1Data : g2Data;
    const block = data.subarray(offset, offset + size);
    offset += size;
    blocks.push(block);
    ecBlocks.push(reedSolomon(block, ecPerBlock));
  }

  const out = new Uint8Array(TOTAL_CODEWORDS[version]!);
  let at = 0;
  const maxData = Math.max(g1Data, g2Data);

  for (let i = 0; i < maxData; i++) {
    for (const block of blocks) {
      if (i < block.length) out[at++] = block[i]!;
    }
  }
  for (let i = 0; i < ecPerBlock; i++) {
    for (const block of ecBlocks) out[at++] = block[i]!;
  }

  return out;
}

/* ------------------------------------------------------------------ */
/* Matrix                                                              */
/* ------------------------------------------------------------------ */

class Matrix {
  readonly size: number;
  readonly modules: Uint8Array;
  readonly reserved: Uint8Array;

  constructor(size: number) {
    this.size = size;
    this.modules = new Uint8Array(size * size);
    this.reserved = new Uint8Array(size * size);
  }

  get(x: number, y: number): number {
    return this.modules[y * this.size + x]!;
  }

  set(x: number, y: number, value: number | boolean, reserve = true): void {
    this.modules[y * this.size + x] = value ? 1 : 0;
    if (reserve) this.reserved[y * this.size + x] = 1;
  }

  /**
   * Mark a module as belonging to a function pattern without touching its value.
   *
   * Needed where two patterns overlap: (6,8) and (8,6) are timing-pattern
   * crossings that must be protected from the mask and data passes, but they
   * must keep the dark value the timing pattern gave them. Reserving them by
   * writing 0 breaks the timing run, and a scanner uses that run to find the
   * grid — the symbol still looks right and still fails to decode.
   */
  reserveOnly(x: number, y: number): void {
    this.reserved[y * this.size + x] = 1;
  }

  isReserved(x: number, y: number): boolean {
    return this.reserved[y * this.size + x] === 1;
  }
}

function placeFunctionPatterns(m: Matrix, version: number): void {
  const size = m.size;

  // Finder patterns and their separators.
  //
  // The 7x7 is dark at distance 0, 1 and 3 from the centre, and light at
  // distance 2. Distance 4 is the separator and is light too. Getting this
  // backwards yields a matrix that is the right size and completely unscannable.
  const finderAt = (cx: number, cy: number): void => {
    for (let dy = -4; dy <= 4; dy++) {
      for (let dx = -4; dx <= 4; dx++) {
        const x = cx + dx;
        const y = cy + dy;
        if (x < 0 || y < 0 || x >= size || y >= size) continue;
        const ring = Math.max(Math.abs(dx), Math.abs(dy));
        m.set(x, y, ring === 2 || ring === 4 ? 0 : 1);
      }
    }
  };
  finderAt(3, 3);
  finderAt(size - 4, 3);
  finderAt(3, size - 4);

  // Timing patterns.
  for (let i = 8; i < size - 8; i++) {
    m.set(i, 6, i % 2 === 0);
    m.set(6, i, i % 2 === 0);
  }

  // Alignment patterns, skipping the three that would collide with a finder.
  const centres = ALIGNMENT[version]!;
  for (const cy of centres) {
    for (const cx of centres) {
      const nearFinder =
        (cx === 6 && cy === 6) ||
        (cx === 6 && cy === size - 7) ||
        (cx === size - 7 && cy === 6);
      if (nearFinder) continue;

      for (let dy = -2; dy <= 2; dy++) {
        for (let dx = -2; dx <= 2; dx++) {
          m.set(cx + dx, cy + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
        }
      }
    }
  }

  // Reserve the format information areas. The bits themselves are written after
  // masking, because the mask pattern is part of what they encode — but every
  // module they will occupy must be reserved *now*, or the mask pass and the
  // data pass both stomp on them. The second copy runs along row 8 and then
  // down column 8, and that vertical half is easy to forget: leaving it
  // unmarked makes the symbol the right size, scans at nothing, and differs from
  // a correct encoder in exactly seven modules.
  for (let i = 0; i <= 8; i++) {
    if (i === 6) {
      // Timing-pattern crossings: protect, but keep them dark.
      m.reserveOnly(8, i);
      m.reserveOnly(i, 8);
      continue;
    }
    m.set(8, i, 0);
    m.set(i, 8, 0);
  }
  for (let i = 0; i < 8; i++) m.set(size - 1 - i, 8, 0);
  for (let i = 0; i < 7; i++) m.set(8, size - 7 + i, 0);

  if (version >= 7) {
    // Version information, 18 bits, twice.
    const bits = versionBits(version);
    for (let i = 0; i < 18; i++) {
      const bit = (bits >>> i) & 1;
      m.set(Math.floor(i / 3), size - 11 + (i % 3), bit);
      m.set(size - 11 + (i % 3), Math.floor(i / 3), bit);
    }
  }

  // The always-dark module.
  m.set(8, size - 8, 1);
}

const MASK_FUNCTIONS: ReadonlyArray<(x: number, y: number) => boolean> = [
  (x, y) => (x + y) % 2 === 0,
  (x, y) => y % 2 === 0,
  (x) => x % 3 === 0,
  (x, y) => (x + y) % 3 === 0,
  (x, y) => (Math.floor(y / 2) + Math.floor(x / 3)) % 2 === 0,
  (x, y) => ((x * y) % 2) + ((x * y) % 3) === 0,
  (x, y) => (((x * y) % 2) + ((x * y) % 3)) % 2 === 0,
  (x, y) => (((x + y) % 2) + ((x * y) % 3)) % 2 === 0,
];

/** BCH(18,6) version information. */
function versionBits(version: number): number {
  let remainder = version << 12;
  for (let i = 17; i >= 12; i--) {
    if ((remainder >>> i) & 1) remainder ^= 0x1f25 << (i - 12);
  }
  return (version << 12) | remainder;
}

/** BCH(15,5) format information, masked with 0x5412. Level M is 0b00. */
function formatBits(mask: number): number {
  const data = (0b00 << 3) | mask;
  let remainder = data << 10;
  for (let i = 14; i >= 10; i--) {
    if ((remainder >>> i) & 1) remainder ^= 0x537 << (i - 10);
  }
  return ((data << 10) | remainder) ^ 0x5412;
}

function placeFormatInfo(m: Matrix, mask: number): void {
  const size = m.size;
  const bits = formatBits(mask);

  for (let i = 0; i < 15; i++) {
    const bit = (bits >>> i) & 1;
    // Copy 1, around the top-left finder.
    if (i < 6) m.set(8, i, bit);
    else if (i === 6) m.set(8, 7, bit);
    else if (i === 7) m.set(8, 8, bit);
    else if (i === 8) m.set(7, 8, bit);
    else m.set(14 - i, 8, bit);

    // Copy 2, split between the other two finders.
    if (i < 8) m.set(size - 1 - i, 8, bit);
    else m.set(8, size - 15 + i, bit);
  }
}

function placeData(m: Matrix, codewords: Uint8Array): void {
  const size = m.size;
  let bitIndex = 0;
  let upward = true;

  for (let right = size - 1; right >= 1; right -= 2) {
    // Column 6 is the vertical timing pattern and is skipped entirely.
    if (right === 6) right = 5;

    for (let step = 0; step < size; step++) {
      const y = upward ? size - 1 - step : step;
      for (const x of [right, right - 1]) {
        if (m.isReserved(x, y)) continue;

        const byte = codewords[bitIndex >>> 3] ?? 0;
        const bit = (byte >>> (7 - (bitIndex & 7))) & 1;
        m.set(x, y, bit, false);
        bitIndex++;
      }
    }
    upward = !upward;
  }
}

function applyMask(m: Matrix, mask: number): void {
  const fn = MASK_FUNCTIONS[mask]!;
  for (let y = 0; y < m.size; y++) {
    for (let x = 0; x < m.size; x++) {
      if (m.isReserved(x, y)) continue;
      if (fn(x, y)) m.set(x, y, m.get(x, y) ^ 1, false);
    }
  }
}

/** The four penalty rules from the specification, scored in one pass. */
function penalty(m: Matrix): number {
  const size = m.size;
  let score = 0;

  // Rule 1: runs of five or more identical modules in a row or column.
  for (let i = 0; i < size; i++) {
    let runRow = 1;
    let runCol = 1;
    for (let j = 1; j < size; j++) {
      runRow = m.get(j, i) === m.get(j - 1, i) ? runRow + 1 : 1;
      if (runRow === 5) score += 3;
      else if (runRow > 5) score += 1;

      runCol = m.get(i, j) === m.get(i, j - 1) ? runCol + 1 : 1;
      if (runCol === 5) score += 3;
      else if (runCol > 5) score += 1;
    }
  }

  // Rule 2: 2x2 blocks of one colour.
  for (let y = 0; y < size - 1; y++) {
    for (let x = 0; x < size - 1; x++) {
      const v = m.get(x, y);
      if (v === m.get(x + 1, y) && v === m.get(x, y + 1) && v === m.get(x + 1, y + 1)) score += 3;
    }
  }

  // Rule 3: finder-like 1:1:3:1:1 patterns with four light modules beside them.
  const PATTERN = [1, 0, 1, 1, 1, 0, 1];
  const matches = (get: (i: number) => number, at: number): boolean => {
    for (let k = 0; k < 7; k++) if (get(at + k) !== PATTERN[k]) return false;
    return true;
  };

  for (let i = 0; i < size; i++) {
    for (let j = 0; j <= size - 7; j++) {
      const row = (k: number) => m.get(k, i);
      const col = (k: number) => m.get(i, k);

      if (matches(row, j)) {
        const beforeClear = j >= 4 && [0, 1, 2, 3].every((k) => m.get(j - 4 + k, i) === 0);
        const afterClear = j + 11 <= size && [7, 8, 9, 10].every((k) => m.get(j + k, i) === 0);
        if (beforeClear || afterClear) score += 40;
      }
      if (matches(col, j)) {
        const beforeClear = j >= 4 && [0, 1, 2, 3].every((k) => m.get(i, j - 4 + k) === 0);
        const afterClear = j + 11 <= size && [7, 8, 9, 10].every((k) => m.get(i, j + k) === 0);
        if (beforeClear || afterClear) score += 40;
      }
    }
  }

  // Rule 4: deviation from a 50% dark ratio.
  let dark = 0;
  for (let i = 0; i < m.modules.length; i++) dark += m.modules[i]!;
  const percent = (dark * 100) / m.modules.length;
  score += Math.floor(Math.abs(percent - 50) / 5) * 10;

  return score;
}

/* ------------------------------------------------------------------ */
/* Public API                                                          */
/* ------------------------------------------------------------------ */

export interface QrCode {
  readonly size: number;
  readonly version: number;
  /** Row-major module values, 1 = dark. */
  readonly modules: Uint8Array;
}

/**
 * Encode `text` as a QR code.
 *
 * Throws rather than truncating when the payload is too long: a truncated link
 * would produce a QR code that scans cleanly and sends guests to the wrong
 * event, which is the worst possible outcome for printed signage.
 */
export function encodeQr(text: string): QrCode {
  const bytes = new TextEncoder().encode(text);
  const version = chooseVersion(bytes.length);
  const codewords = interleave(buildCodewords(bytes, version), version);

  const size = version * 4 + 17;
  const base = new Matrix(size);
  placeFunctionPatterns(base, version);
  placeData(base, codewords);

  // Try every mask and keep the least-penalised, as the specification requires.
  let best: Matrix | null = null;
  let bestScore = Number.POSITIVE_INFINITY;

  for (let mask = 0; mask < 8; mask++) {
    const candidate = new Matrix(size);
    candidate.modules.set(base.modules);
    candidate.reserved.set(base.reserved);

    applyMask(candidate, mask);
    placeFormatInfo(candidate, mask);

    const score = penalty(candidate);
    if (score < bestScore) {
      bestScore = score;
      best = candidate;
    }
  }

  return { size, version, modules: best!.modules };
}

/** Render a QR code as an SVG string. Scales to any size with no resampling. */
export function qrToSvg(qr: QrCode, quietZone = 4): string {
  const dimension = qr.size + quietZone * 2;
  const path: string[] = [];

  for (let y = 0; y < qr.size; y++) {
    for (let x = 0; x < qr.size; x++) {
      if (qr.modules[y * qr.size + x]) path.push(`M${x + quietZone} ${y + quietZone}h1v1h-1z`);
    }
  }

  return [
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${dimension} ${dimension}"`,
    ` shape-rendering="crispEdges" role="img" aria-label="QR code">`,
    `<rect width="${dimension}" height="${dimension}" fill="#ffffff"/>`,
    `<path d="${path.join("")}" fill="#000000"/>`,
    `</svg>`,
  ].join("");
}
